import { Drawer as AntdDrawer } from 'antd';
import type { ReactNode } from 'react';

import type { ButtonVariant } from '@/components/common/button';

import { OverlayChrome, OverlayFooter } from '../overlay';

import styles from './drawer.module.scss';

export type DrawerProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  width?: number | string;
  maskClosable?: boolean;
  closeOnEsc?: boolean;
  loading?: boolean;
  /** Read-only panels (no actions) omit the footer entirely — just the header close (x). */
  hideFooter?: boolean;
  /** Renders the body as a rounded-top white card over the cream header (see `OverlayChrome`). */
  roundedBody?: boolean;
  /** Disables the primary/submit footer button — e.g. a form drawer whose consumer keeps Create off
   *  until every field is valid. */
  primaryDisabled?: boolean;
  children: ReactNode;
  primaryText?: string;
  onPrimary?: () => void;
  primaryVariant?: ButtonVariant;
  primaryDanger?: boolean;
  cancelText?: string;
  onCancel?: () => void;
  /** Left-aligned footer node, e.g. a filter panel's Clear Filter / Reset. */
  footerLeft?: ReactNode;
  /** Internal: used by FormDrawer to wrap body + footer in a <form>. */
  onSubmit?: React.ComponentProps<'form'>['onSubmit'];
  /** Internal: makes the primary footer button type="submit". */
  primaryIsSubmit?: boolean;
};

export function Drawer({
  open,
  onClose,
  title,
  width = 464,
  maskClosable = true,
  closeOnEsc = true,
  loading = false,
  hideFooter = false,
  roundedBody = false,
  primaryDisabled = false,
  children,
  primaryText,
  onPrimary,
  primaryVariant = 'neutral',
  primaryDanger,
  cancelText = 'Cancel',
  onCancel,
  footerLeft,
  onSubmit,
  primaryIsSubmit = false,
}: DrawerProps) {
  const footer = hideFooter ? undefined : (
    <OverlayFooter
      primaryText={primaryText}
      onPrimary={onPrimary}
      isSubmit={primaryIsSubmit}
      primaryVariant={primaryVariant}
      primaryDanger={primaryDanger}
      disabled={primaryDisabled}
      loading={loading}
      cancelText={cancelText}
      onCancel={onCancel ?? onClose}
      footerLeft={footerLeft}
    />
  );

  return (
    <AntdDrawer
      open={open}
      onClose={onClose}
      placement="right"
      width={width}
      mask={{ closable: maskClosable }}
      keyboard={closeOnEsc}
      closable={false}
      title={null}
      footer={null}
      destroyOnHidden
      className={styles.drawer}
      classNames={{ body: styles.bodyReset }}
    >
      <OverlayChrome
        title={title}
        onClose={onClose}
        footer={footer}
        onSubmit={onSubmit}
        roundedBody={roundedBody}
      >
        {children}
      </OverlayChrome>
    </AntdDrawer>
  );
}
